import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { CVLocalizationService } from "src/app/reusables/services/cv-localization.service";
import { CxoneEtlDataImportService } from "../../cxone-etl-data-imports.service";
import { ImportPayload, ImportResponse } from "./cxone-custom-data-import-dialog.model";
import { ToastrManager } from "@niceltd/cxone-components/toastr";
import { BehaviorSubject, Subscription, combineLatest } from "rxjs";
import { MessageModalComponent } from "@niceltd/cxone-components/modal";

const ALLOWED_FILE_SIZE = 15;
interface FeedList {
    FeedName: string;
    SourceSystem: string;
}
@Component({
    selector: "cv-cxone-custom-data-import-dialog",
    templateUrl: "./cxone-custom-data-import-dialog.component.html",
    styleUrls: ["./cxone-custom-data-import-dialog.component.scss"],
})
export class CxoneCustomDataImportDialogComponent implements OnInit, OnDestroy {
    feedList: FeedList[] = [];
    importForm = new FormGroup({
        feed: new FormControl(null, [Validators.required]),
        file: new FormControl("", [Validators.required]),
    });
    selectedFileName: string;
    validationError: string;
    validationErrorType: number;
    headerTitle: string;
    loading: boolean;
    validatingColumns: boolean;
    uploadedFileColumns: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    fileValiditySubscription: Subscription;
    placeholderText = '';

    constructor(
        private dialogSrvc: DialogService,
        private dialogRef: DynamicDialogRef,
        private cvLocalizationService: CVLocalizationService,
        private cxoneEtlDataImportService: CxoneEtlDataImportService,
        private dialogConfig: DynamicDialogConfig
    ) { }

    get feed(): FeedList {
        return this.importForm.get("feed").value;
    }

    get file(): any {
        return this.importForm.get("file").value;
    }

    async ngOnInit() {
        await this.loadLocalization();
        this.feedList = this.dialogConfig.data.rawDataDefinitions;
        this.initializeFileColumnValidator();
    }
    async loadLocalization() {
        try {
            await this.cvLocalizationService.GetSection('CXone_Custom_Data_Import');
        }
        catch {
            console.log('Failed to load localization for CXone_Custom_Data_Import');
        }
        finally {
            this.headerTitle = this.getLocaleText("Import_Data", "Import Data");
            this.placeholderText = this.getLocalizationString("Select_Item", "Select...");
        }
    }

    cancel(): void {
        this.dialogRef.close();
    }

    async save(): Promise<void> {
        this.loading = true;
        const payload: ImportPayload = {
            File: this.file,
            Feed_Name: this.feed.FeedName,
            Source_System: this.feed.SourceSystem,
        };
        try {
            const importResponse: ImportResponse = await this.cxoneEtlDataImportService.importFile(payload);
            this.dialogRef.close(importResponse);
        } catch (error) {
            //When there are no vallid reoords in the file, backend will throw an error as Bad Request(400)
            if (error.status_code == 400) {
                this.dialogRef.close();
                this.failedImportError();
            }
            else {
                this.invalidFileError();
            }
        } finally {
            this.loading = false;
        }
    }

    failedImportError(): void {
        const alertTitile = this.getLocalizationString("Failed_Import_Title", "All Invalid Records");
        const alertMessage = this.getLocalizationString("Failed_Import_Message", "Could not import records.");
        this.showAlertMessage(alertTitile, alertMessage);
        this.loading = false;
    }

    getLocalizationString(key: string, defaultValue: string): string {
        const baseString = this.cvLocalizationService.getCachedText("CXone_Custom_Data_Import", key, defaultValue);
        return baseString;
    }

    invalidFileError(): void {
        this.validationErrorType = 0;
        this.validationError = this.getLocaleText(
            "Invalid_File",
            "Selected file is not supported."
        );
        this.loading = false;
    }

    uploadFile(event): void {
        this.validationError = null;
        this.selectedFileName = event.target.files[0].name;
        const ext = this.selectedFileName.split(".")[this.selectedFileName.split(".").length - 1];
        if (ext !== 'csv' && ext !== 'txt') {
            this.invalidFileError();
            return;
        }
        const file = event.target.files[0];
        const fileSizeInMB = file.size / (1024 * 1024);
        if (fileSizeInMB > ALLOWED_FILE_SIZE) {
            this.validationErrorType = 0;
            this.validationError = this.getLocaleText("File_Size_Error", "File size cannot exceed 15 MB");
            return;
        } else {
            this.validationError = null;
        }
        // Read the file and check if it has any data
        const reader = new FileReader();
        reader.onload = (e) => {
            const content = e.target.result as string;
            switch (true) {
                case content.trim() === "":
                    this.validationErrorType = 1;
                    this.validationError = this.getLocaleText("No_Records_Error", "No records in the selected file");
                    break;
                case ext.toLowerCase() === "csv":
                    // Specific validation for CSV to ensure there is more than one line
                    const lines = content.split("\n");
                    if (lines.length <= 1 || (lines.length === 2 && lines[1].trim() === "")) {
                        this.validationErrorType = 1;
                        this.validationError = this.getLocaleText("No_Records_Error", "No records in the selected file");
                        return;
                    } else {
                        this.validationError = null;
                    }
                    break;
                default:
                    this.validationError = null;
                    break;
            }
            if (!!this.validationError) {
                this.importForm.get("file").patchValue("");
                return;
            }
            this.uploadedFileColumns.next(this.getColumns(content));
            this.importForm.get("file").patchValue(content);
        };
        reader.readAsText(file);
    }

    clearFile(uploader): void {
        this.selectedFileName = null;
        uploader.value = "";
        this.importForm.get("file").patchValue("");
        this.validationError = null;
    }

    getLocaleText(key: string, defaultValue?: string): string {
        return this.cvLocalizationService.getCachedText(
            "CXone_Custom_Data_Import",
            key,
            defaultValue
        );
    }

    getColumns(content: string): string[] {
        const lines = content.split("\n");
        if (lines.length > 0) {
            return lines[0].split(",").map((column) => column.trim());
        }
        return [];
    }

    initializeFileColumnValidator(): void {
        this.fileValiditySubscription = combineLatest([
            this.uploadedFileColumns.asObservable(),
            this.importForm.valueChanges,
        ]).subscribe(([uploadedFileColumns, form]) => {
            if (form.feed && form.file && uploadedFileColumns.length) {
                this.validatingColumns = true;
                const feedColumns = Object.keys(form.feed.FieldTypeMapping);
                const validColumns = uploadedFileColumns.length === feedColumns.length &&
                    feedColumns.every((col) => uploadedFileColumns.includes(col));
                if (!validColumns) {
                    this.validationErrorType = 1;
                    this.validationError = this.getLocaleText(
                        "Mismatch_Values",
                        "The expected values are not present in the selected file."
                    );
                } else {
                    this.validationError = null;
                }
                this.validatingColumns = false;
            }
            this.validatingColumns = false;
        });
    }

    ngOnDestroy(): void {
        this.fileValiditySubscription.unsubscribe();
    }

    public showAlertMessage(alertTitile: string, alertMessage: string) {
        const dialogData = {
            title: alertTitile,
            type: 'user-warning',
            message: `<div class="msg-line-1"> ${alertMessage} </div>`,
            closeButtonText: this.getLocalizationString("Okay", "Okay"),
        };
        const ref = this.dialogSrvc.open(MessageModalComponent, {
            width: '420px',
            height: '184px',
            styleClass: 'cx-one-ui modal-center testing',
            data: dialogData
        });

        ref.onClose.subscribe(x => {
            if (x) {
                this.loading = false;
            }
        });
    }
}
